import { FirebaseClient } from '@/api/firebaseClient';
import { activeFestivalEditionQuery, QUERY_KEYS } from '@/api/queries';
import { useInvalidatingMutation } from '@/hooks/useInvalidatingMutation';
import { PlaceBusOrderRequest } from '@packages/types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Container, SectionRule } from '@/components';
import { useTranslation } from 'react-i18next';
import { Cross1Icon } from '@radix-ui/react-icons';
import { isTicketingUnlocked, useTicketing } from '@/hooks/useTicketing';
import { useBus } from '@/hooks/useBus';
import { useQuery } from '@tanstack/react-query';

type TicketItem = PlaceBusOrderRequest['tickets'][number] & { rowId: string };

const StatePanel = ({ title, message }: { title: string; message: string }) => {
  const { t } = useTranslation();
  return (
    <Container max={760}>
      <h1 className="font-disp text-6xl uppercase text-ww-charcoal sm:text-7xl">
        {t('bus_tickets')}
      </h1>
      <div
        className="mt-8 p-7"
        style={{
          background: 'var(--paper-50)',
          border: '2px solid var(--ww-charcoal)',
          boxShadow: '5px 5px 0 var(--ww-charcoal)',
        }}>
        <h2 className="font-sans text-2xl font-bold text-ww-charcoal">{title}</h2>
        <p className="mt-2 font-sans text-ink-soft">{message}</p>
      </div>
    </Container>
  );
};

export const BusTicketsPage = () => {
  const { t } = useTranslation();
  const { ticketToken } = useTicketing();
  const { data: activeEdition, isLoading: isActiveEditionLoading } = useQuery(
    activeFestivalEditionQuery(),
  );

  const navigate = useNavigate();

  const ticketsRef = useRef<HTMLDivElement>(null);

  const client = new FirebaseClient();

  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  const [tickets, setTickets] = useState<TicketItem[]>([]);

  const { busses, isLoading } = useBus(activeEdition?.festivalEditionId);

  const availableBusses = useMemo(() => {
    return (busses ?? []).map((bus) => {
      return {
        ...bus,
        seatsAvailable:
          bus.seatsAvailable - (tickets.filter((t) => t.busId === bus.busId).length ?? 0),
      };
    });
  }, [busses, tickets]);

  const departureBusses = useMemo(
    () => availableBusses.filter((b) => b.type === 'departure'),
    [availableBusses],
  );

  const arrivalBusses = useMemo(
    () => availableBusses.filter((b) => b.type === 'arrival'),
    [availableBusses],
  );

  const ticketsByBusId = useMemo(
    () =>
      tickets.reduce<Record<string, TicketItem[]>>((acc, ticket) => {
        if (!acc[ticket.busId]) {
          acc[ticket.busId] = [];
        }
        acc[ticket.busId].push(ticket);
        return acc;
      }, {}),
    [tickets],
  );

  const { mutateAsync: placeBusOrder } = useInvalidatingMutation([QUERY_KEYS.BUS], {
    mutationFn: async (data: PlaceBusOrderRequest) => client.placeBusOrder(data),
  });

  const totalPrice = useMemo(
    () =>
      tickets.reduce(
        (acc, ticket) => acc + (busses?.find((b) => b.busId === ticket.busId)?.price ?? 0),
        0,
      ),
    [tickets, busses],
  );

  const addTicket = (busId: string) => {
    setTickets((prev) => [
      ...prev,
      {
        email: '',
        name: '',
        busId,
        rowId: Math.random().toString(36).substring(7),
      },
    ]);

    setTimeout(() => {
      ticketsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
    }, 100);
  };

  const onSubmit = async () => {
    setError('');
    const form: HTMLFormElement | null = document.getElementById(
      'ticketForm',
    ) as HTMLFormElement | null;
    if (!form) return;
    const isValid = form.reportValidity();
    if (!isValid) return;

    if (!tickets.length) {
      setError(t('pleaseAddTicket'));
      return;
    }

    try {
      const result = await placeBusOrder({
        owner: { kind: 'activeFestivalEdition' },
        tickets,
        email,
      });
      navigate(`/bus_orders/${result.data.busOrder.orderId}`);
    } catch (e) {
      console.error(t('errorBuyingTicket'), e);
      setError(t('ticketCouldNotBeBought'));
    }
  };

  const updateTicket = (ticket: TicketItem) => {
    setTickets((prev) => prev.map((t) => (t.rowId === ticket.rowId ? ticket : t)));
  };

  const deleteTicket = (ticket: TicketItem) => {
    setTickets((prev) => prev.filter((t) => t.rowId !== ticket.rowId));
  };

  useEffect(() => {
    if (tickets[0]?.email) {
      setEmail(tickets[0]?.email ?? '');
    }
  }, [tickets, setEmail]);

  if (isActiveEditionLoading || isLoading) {
    return null;
  }

  if (!activeEdition) {
    return (
      <StatePanel
        title={t('bus_tickets_not_open_title')}
        message={t('bus_tickets_not_open_message')}
      />
    );
  }

  const unlocked = isTicketingUnlocked(activeEdition.accessToken, ticketToken);

  if (!unlocked) {
    return (
      <StatePanel
        title={t('bus_tickets_not_open_title')}
        message={t('bus_tickets_not_open_message')}
      />
    );
  }

  if (activeEdition.busSaleState !== 'open') {
    return (
      <StatePanel title={t('bus_tickets_soon_title')} message={t('bus_tickets_prepared_message')} />
    );
  }

  if (availableBusses.length === 0) {
    return <StatePanel title={t('bus_tickets')} message={t('noTicketsAvailable')} />;
  }

  const busItem = (bus: (typeof arrivalBusses)[number]) => {
    const isSoldOut = bus.seatsAvailable <= 0 || bus.saleState === 'soldOut';
    const rows = ticketsByBusId[bus.busId] ?? [];

    return (
      <article
        key={bus.busId}
        className="ww-scrap ww-scrap--peri"
        style={{
          background: 'var(--paper-50)',
          border: '2px solid var(--ww-charcoal)',
          boxShadow: '5px 5px 0 var(--ww-charcoal)',
          padding: '22px 24px',
        }}>
        <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <h3 className="font-sans text-2xl font-bold tracking-[0.02em] text-ww-charcoal">
            {bus.label}
          </h3>
          <div className="whitespace-nowrap font-mono text-2xl font-bold text-ww-orange">
            {bus.price} €{' '}
            <span className="font-sans text-xs font-semibold uppercase tracking-[0.06em] text-ink-soft">
              {t('per_person_short')}
            </span>
          </div>
        </header>

        {rows.length ? (
          <ul
            className="flex flex-col gap-3.5"
            style={{ borderTop: '2px solid var(--ww-charcoal)', paddingTop: '16px' }}>
            {rows.map((ticket, index) => (
              <li key={ticket.rowId} className="flex items-end gap-3">
                <div className="grid grow grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="ww-field m-0 block">
                    <span className="ww-label">
                      {t('full_name')} · {t('person')} {index + 1}
                    </span>
                    <Input
                      name="name"
                      required
                      type="text"
                      value={ticket.name}
                      onChange={(input) => updateTicket({ ...ticket, name: input.target.value })}
                      placeholder={t('full_name')}
                    />
                  </label>
                  <label className="ww-field m-0 block">
                    <span className="ww-label">{t('email')}</span>
                    <Input
                      name="email"
                      required
                      type="email"
                      value={ticket.email}
                      onChange={(input) => updateTicket({ ...ticket, email: input.target.value })}
                      placeholder="name@mail.de"
                    />
                  </label>
                </div>
                <button
                  type="button"
                  aria-label={t('remove_person')}
                  className="mb-1 flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-ww-charcoal text-paper-50"
                  onClick={() => deleteTicket(ticket)}>
                  <Cross1Icon />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="font-sans text-sm text-ink-soft">{t('bus_ticket_no_person_added_note')}</p>
        )}

        <button
          type="button"
          className="ww-btn ww-btn--ink mt-4"
          onClick={() => addTicket(bus.busId)}
          disabled={isSoldOut}>
          {isSoldOut ? t('sold_out') : t('add_person')}
        </button>
      </article>
    );
  };

  const outwardCount = tickets.filter((t) => arrivalBusses.find((b) => b.busId === t.busId)).length;
  const returnCount = tickets.filter((t) =>
    departureBusses.find((b) => b.busId === t.busId),
  ).length;

  return (
    <Container max={1180}>
      <div className="ww-eyebrow" style={{ color: 'var(--ww-orange)' }}>
        {t('order_tickets')}
      </div>
      <h1 className="mb-6 mt-2 font-disp text-6xl uppercase text-ww-charcoal sm:text-7xl">
        {t('bus_tickets')}
      </h1>

      <p
        className="mb-9 p-4 font-sans text-sm text-ink-soft"
        style={{ background: 'var(--paper-50)', border: '2px solid var(--ww-charcoal)' }}>
        {t('bus_tickets_hint_beginning')}
      </p>

      <form id="ticketForm" className="grid grid-cols-1 gap-12 lg:grid-cols-[2fr_1fr]">
        {/* LEFT */}
        <div>
          <SectionRule>{t('outward')}</SectionRule>
          <p className="mb-5 font-sans text-sm text-ink-soft">{t('bus_outward_note')}</p>
          <section className="mb-11 flex flex-col gap-6">
            {(arrivalBusses ?? []).map(busItem)}
          </section>

          <SectionRule accent="var(--ww-orange)">{t('return')}</SectionRule>
          <p className="mb-5 font-sans text-sm text-ink-soft">{t('bus_return_note')}</p>
          <section className="mb-11 flex flex-col gap-6">
            {(departureBusses ?? []).map(busItem)}
          </section>

          <SectionRule>{t('order_email')}</SectionRule>
          <label className="ww-field block">
            <span className="ww-label">{t('order_email')}</span>
            <Input
              name="orga"
              required
              type="email"
              value={email}
              onChange={(input) => setEmail(input.target.value)}
              placeholder="name@mail.de"
            />
          </label>
        </div>

        {/* RIGHT — sticky Zusammenfassung */}
        <aside>
          <div
            className="ww-grain ww-grain--soft sticky top-6"
            style={{
              background: 'var(--ww-charcoal)',
              color: 'var(--paper-50)',
              border: '3px solid var(--ww-charcoal)',
              boxShadow: 'var(--shadow-lift)',
              padding: '26px 26px 30px',
              transform: 'rotate(-1deg)',
            }}>
            <div className="ww-eyebrow" style={{ color: 'var(--ww-chartreuse)' }}>
              {t('summary')}
            </div>
            <table className="mt-4 w-full border-collapse font-mono text-sm">
              <tbody>
                <tr style={{ borderBottom: '1px dashed rgba(250,250,248,.25)' }}>
                  <td className="py-2 text-paper-200">{t('outward')}</td>
                  <td className="py-2 text-right">{outwardCount}</td>
                </tr>
                <tr style={{ borderBottom: '1px dashed rgba(250,250,248,.25)' }}>
                  <td className="py-2 text-paper-200">{t('return')}</td>
                  <td className="py-2 text-right">{returnCount}</td>
                </tr>
              </tbody>
            </table>
            <div className="mt-5">
              <div className="ww-eyebrow" style={{ color: 'var(--ww-chartreuse)' }}>
                {t('totalPrice')}
              </div>
              <div className="font-mono text-[46px] font-bold leading-none tracking-[-0.02em] text-paper-50">
                {totalPrice} €
              </div>
            </div>
            <button
              type="button"
              onClick={() => onSubmit()}
              disabled={!tickets.length}
              className="ww-btn ww-btn--primary ww-btn--block ww-btn--lg mt-5">
              {t('order_tickets')} →
            </button>
            {error ? <p className="mt-2 font-sans text-sm text-ww-orange">{error}</p> : null}
          </div>
        </aside>
      </form>
    </Container>
  );
};
